import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, SectionLabel } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SIZES = ["20–50", "51–150", "151–300", "301–500", "500+"];

const Field = ({ label, children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block">
    <span className="mb-2 block text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">{label}</span>
    {children}
  </label>
);

const inputCls =
  "w-full border-b-2 border-white/40 bg-transparent py-3 text-base text-white placeholder-white/30 outline-none transition-colors duration-200 focus:border-white";

export const DemoForm = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", company_size: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/demo-requests`, form);
      setDone(true);
    } catch (err) {
      toast.error("Something went wrong sending your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="demo" data-testid="demo-section" className="grid-lines-dark bg-black text-white">
      <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-36">
        <SectionLabel index="07" title="Book a demo" dark />
        <div className="mt-10 grid gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <h2 className="text-[28px] font-bold leading-tight tracking-tight md:text-[40px]">
              See what your company is contradicting itself about.{" "}
              <span className="text-white/40">This week.</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/65 md:text-lg">
              Thirty minutes. Bring your hardest cross-system question — the one that currently takes
              three meetings to answer — and we'll show you what Qwiri catches.
            </p>
            <div className="mt-10 space-y-4 border-t border-white/20 pt-8 text-sm text-white/60">
              <p><span className="font-bold text-white">For whom:</span> founders, COOs and CTOs at 20–500 person companies.</p>
              <p><span className="font-bold text-white">What you keep:</span> the drift report we generate from your connected sources.</p>
              <p><span className="font-bold text-white">What we never do:</span> widen your permissions, or score your people. Attribution yes, evaluation no.</p>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            {done ? (
              <div data-testid="demo-success" className="hard-shadow-white flex h-full flex-col justify-center border-2 border-white p-10">
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/60">Request received</div>
                <h3 className="mt-4 text-3xl font-extrabold tracking-tight md:text-4xl">We'll be in touch.</h3>
                <p className="mt-4 max-w-sm text-base leading-relaxed text-white/65">
                  Your demo request is in. Expect a reply at{" "}
                  <span className="font-bold text-white">{form.email}</span> within one business day.
                </p>
                <button
                  data-testid="demo-another-btn"
                  onClick={() => { setDone(false); setForm({ name: "", email: "", company: "", company_size: "", message: "" }); }}
                  className="mt-8 w-fit border-2 border-white px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] transition-colors duration-150 hover:bg-white hover:text-black"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form data-testid="demo-form" onSubmit={submit} className="space-y-7 border-2 border-white p-7 hard-shadow-white md:p-10">
                <div className="grid gap-7 sm:grid-cols-2">
                  <Field label="Full name" htmlFor="demo-name">
                    <input id="demo-name" data-testid="demo-name-input" required value={form.name} onChange={set("name")} placeholder="Ada Lovelace" className={inputCls} />
                  </Field>
                  <Field label="Work email" htmlFor="demo-email">
                    <input id="demo-email" data-testid="demo-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="ada@company.com" className={inputCls} />
                  </Field>
                </div>
                <div className="grid gap-7 sm:grid-cols-2">
                  <Field label="Company" htmlFor="demo-company">
                    <input id="demo-company" data-testid="demo-company-input" required value={form.company} onChange={set("company")} placeholder="Acme Systems" className={inputCls} />
                  </Field>
                  <Field label="Company size" htmlFor="demo-size">
                    <select id="demo-size" data-testid="demo-size-select" required value={form.company_size} onChange={set("company_size")} className={`${inputCls} cursor-pointer bg-black`}>
                      <option value="" disabled>Select size</option>
                      {SIZES.map((s) => (
                        <option key={s} value={s}>{s} people</option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field label="What challenge should we focus on? (optional)" htmlFor="demo-message">
                  <textarea id="demo-message" data-testid="demo-message-input" rows={3} value={form.message} onChange={set("message")} placeholder="e.g. Deals get promised before engineering knows they exist…" className={`${inputCls} resize-none`} />
                </Field>
                <button
                  data-testid="demo-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="w-full border-2 border-white bg-white py-4 text-[12px] font-bold uppercase tracking-[0.25em] text-black transition-colors duration-150 hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? "Sending…" : "Book a demo"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
